import messages, { translate } from "@/plugins/i18n";
import { isNotNullOrUndefined } from "@/app/apiutils";
import { defineComponent } from "vue";
import type BaseEnumType from "@/app/services/baseEnumType";
import Key from "@/app/types/common/key";

export const DisplayHelpers = defineComponent({
  methods: {
    displayString(value: any): string {
      if (value == null || (typeof value === "string" && !value.trim())) {
        return "-";
      }
      return value;
    },
    /**
     * Display an array of strings removing null or undefined.
     */
    displayStringArray(
      value: (string | null | undefined)[],
      separator?: string | undefined
    ): string {
      const innerValue = value
        .filter(isNotNullOrUndefined)
        .filter((val) => val.length > 0);

      if (!innerValue || innerValue.length === 0) {
        return "-";
      }

      if (innerValue.length === 1) {
        return innerValue[0];
      }

      return innerValue.join(separator ?? ", ");
    },

    displayBool(value: any): string {
      if (!value) {
        return messages.no;
      }

      if (typeof value === "boolean") {
        return value ? messages.yes : messages.no;
      }

      return value;
    },
  },
});

interface DisplayHelpersOptions {
  defaultValue?: string;
}

export const displayString = (
  value: any,
  opts?: DisplayHelpersOptions
): string => {
  if (value == null || (typeof value === "string" && !value.trim())) {
    return opts?.defaultValue ?? "-";
  }
  return value;
};

/**
 * Display an array of strings removing null or undefined.
 */
export const displayStringArray = (
  value: (string | null | undefined)[],
  separator?: string | undefined
) => {
  const innerValue = value
    .filter(isNotNullOrUndefined)
    .filter((val) => val.length > 0);

  if (!innerValue || innerValue.length === 0) {
    return "-";
  }

  if (innerValue.length === 1) {
    return innerValue[0];
  }

  return innerValue.join(separator ?? ", ");
};

export const displayBool = (value: any): string => {
  if (!value) {
    return messages.no;
  }

  if (typeof value === "boolean") {
    return value ? messages.yes : messages.no;
  }

  return value;
};

export const displayUserInitials = (userName: string) => {
  let names = userName.split(" "),
    initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }
  return initials;
};

export const displayEnumArray = <T extends BaseEnumType>(
  values: Readonly<T[]>,
  separator?: string
) => {
  return displayStringArray(
    values.map((value) =>
      translate(Key.fromString(value.translationKey!)).toString()
    ),
    separator
  );
};

export const displayQuantityVsExpected = (
  value: number | null | undefined,
  expected: number | null | undefined,
  opts?: DisplayHelpersOptions
) => {
  if (value == null) {
    return opts?.defaultValue ?? "-";
  }
  if (expected == null) {
    return value.toString();
  }
  return `${value}/${expected}`;
};

export function useDisplayHelpers(opts?: DisplayHelpersOptions) {
  const displayStringWithOpts = (
    value: Parameters<typeof displayString>["0"]
  ) => displayString(value, opts);

  return {
    displayStringArray,
    displayString: displayStringWithOpts,
    displayBool,
    displayUserInitials,
    displayEnumArray,
    displayQuantityVsExpected,
  };
}
